import {
  AgentStep,
  AgentUsage,
  CitationInfo,
  ContextDocType,
  ContextFile,
  FeedbackReason,
  ModelsResponse,
  SessionSummary,
  SseFrame,
  StoredTurn,
} from './types';
import {
  chainFlow,
  failedSummary,
  finishedSummary,
  liveCount,
  parallelFlow,
  PLANNING,
  restoredSummary,
  restoredTrace,
  stepProgress,
  stoppedSummary,
  THINKING,
  TraceItem,
  TraceStep,
} from './trace';
import { provisionalTitle, renamedTitle } from './titles';
import {
  contextTagLabels,
  ContextSelection,
  EMPTY_SELECTION,
  isEmptySelection,
  removeFile,
  removeType,
  toggleFile,
  toggleType,
} from './context-selection';
import { isPickable, ProjectToolFlags, ToolGate } from './tools';
import { NO_RUN_OPTIONS, RunOptionKind, RunOptions } from './run-options';
import { usageFromTurn } from './usage';

/**
 * All of the chat page's state and every transition (chat-ui-spec §2–§5). It lives in a
 * page-local reducer rather than Redux: answers stream at token rate and only this page reads them.
 * Stream actions are keyed by chat and turn, so a stream keeps writing into its own chat while
 * another one is shown (§3.1).
 */

export interface Feedback {
  rating: 1 | -1 | null;
  reason: FeedbackReason | null;
  note: string;
}

export type BotStatus = 'pending' | 'streaming' | 'done' | 'error' | 'stopped';

export interface BotState {
  status: BotStatus;
  /** Rebuilt from a stored transcript rather than streamed. */
  restored: boolean;
  /** A restored failed or stopped turn, drawn as its own error row (§3.4). */
  errorRow: boolean;
  trace: TraceItem[];
  traceVisible: boolean;
  traceOpen: boolean;
  summary: string;
  budget?: number;
  batch: { calls: number; seen: number };
  /** Steps that have settled, for the failure and stop summaries. */
  doneSteps: number;
  streamedText: string;
  /** Markdown to draw once settled: the server's response, or the partial text when stopped. */
  answer?: string;
  /** Plain text for an error, or a stop before any text arrived. */
  message?: string;
  citations: Partial<CitationInfo>[];
  usage?: AgentUsage;
  turnId?: string;
  feedback: Feedback;
  showFeedback: boolean;
}

export interface Turn {
  key: string;
  question: string;
  toolUsed: string | null;
  contextUsed: { types: string[]; files: string[] } | null;
  /** The project the turn was asked in; its token is used for the turn's feedback. */
  projectId: string | null;
  bot: BotState;
}

export interface Chat {
  localId: number;
  sessionId: string | null;
  projectId: string | null;
  title: string | null;
  /** Its transcript has been fetched, or it never had one. */
  loaded: boolean;
  /** The welcome screen shows until a message is sent or a transcript is restored. */
  welcome: boolean;
  turns: Turn[];
}

export interface ContextState {
  selection: ContextSelection;
  /** null until loaded for `docTypesFor`. */
  docTypes: ContextDocType[] | null;
  docTypesFor: string | null;
  /** Files per `filesCacheKey(docType, term)`. */
  fileCache: Record<string, ContextFile[]>;
  /** The picker's document-type dropdown; kept between opens. */
  docFilter: string;
  pickerOpen: boolean;
}

export interface RunOptionsState extends RunOptions {
  status: 'idle' | 'loading' | 'ready' | 'failed';
  roster: ModelsResponse | null;
}

export interface CenterlineAiState {
  projectId: string | null;
  /** New chats are unshifted; server sessions are appended in server order (§3.2). */
  chats: Chat[];
  activeId: number;
  nextLocalId: number;
  /** One turn at a time across every chat (§2.2). */
  inFlight: { chatId: number; turnKey: string } | null;
  capabilities: { byProject: Record<string, ProjectToolFlags>; dbref?: boolean };
  context: ContextState;
  /** The "+" tool chip; it applies to one message (§2.7). */
  activeTool: string | null;
  runOptions: RunOptionsState;
  sidebarRail: boolean;
}

export type CenterlineAiAction =
  | { type: 'projectSelected'; projectId: string }
  | { type: 'newChat' }
  | { type: 'openChat'; localId: number }
  | { type: 'sessionsLoaded'; projectId: string; sessions: SessionSummary[] }
  | { type: 'sessionRestored'; projectId: string; sessionId: string; turns: StoredTurn[] }
  | { type: 'transcriptRequested'; localId: number }
  | { type: 'transcriptLoaded'; localId: number; turns: StoredTurn[] }
  | { type: 'renameChat'; localId: number; title: string }
  | { type: 'deleteChat'; localId: number }
  | { type: 'toggleRail' }
  | {
      type: 'send';
      chatId: number;
      turnKey: string;
      question: string;
      tool: string | null;
      context: ContextSelection | null;
    }
  | { type: 'frame'; chatId: number; turnKey: string; frame: SseFrame }
  | { type: 'delta'; chatId: number; turnKey: string; text: string }
  | { type: 'turnFailed'; chatId: number; turnKey: string; message: string }
  | { type: 'turnStopped'; chatId: number; turnKey: string }
  | { type: 'turnSettled'; chatId: number; turnKey: string }
  | { type: 'feedback'; chatId: number; turnKey: string; patch: Partial<Feedback> }
  | { type: 'setTool'; tool: string | null }
  | { type: 'ctxToggleType'; docType: string }
  | { type: 'ctxToggleFile'; file: ContextFile }
  | { type: 'ctxRemoveType'; docType: string }
  | { type: 'ctxRemoveFile'; fileId: string }
  | { type: 'ctxClear' }
  | { type: 'ctxDocTypesLoaded'; projectId: string; docTypes: ContextDocType[]; cache: boolean }
  | { type: 'ctxFilesLoaded'; projectId: string; key: string; files: ContextFile[] }
  | { type: 'ctxFilter'; docType: string }
  | { type: 'ctxPicker'; open: boolean }
  | { type: 'rosterLoading' }
  | { type: 'rosterLoaded'; roster: ModelsResponse; options: RunOptions }
  | { type: 'rosterFailed' }
  | { type: 'setRunOption'; kind: RunOptionKind; value: string };

const ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const STALLED_TEXT = 'The connection ended before an answer arrived.';
export const STOPPED_EMPTY_TEXT = 'Stopped before an answer arrived.';
export const NO_ANSWER_TEXT = '(no answer)';

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;
const serverId = (v: unknown): string | null => (typeof v === 'string' && ID_RE.test(v) ? v : null);

const emptyContext = (): ContextState => ({
  selection: EMPTY_SELECTION,
  docTypes: null,
  docTypesFor: null,
  fileCache: {},
  docFilter: '',
  pickerOpen: false,
});

const NO_FEEDBACK: Feedback = { rating: null, reason: null, note: '' };

export const newLiveBot = (): BotState => ({
  status: 'pending',
  restored: false,
  errorRow: false,
  trace: [],
  traceVisible: true,
  traceOpen: true,
  summary: PLANNING,
  batch: { calls: 1, seen: 0 },
  doneSteps: 0,
  streamedText: '',
  citations: [],
  feedback: NO_FEEDBACK,
  showFeedback: false,
});

const blankChat = (localId: number, projectId: string | null): Chat => ({
  localId,
  sessionId: null,
  projectId,
  title: null,
  loaded: true,
  welcome: true,
  turns: [],
});

export function initialCenterlineAiState(): CenterlineAiState {
  return {
    projectId: null,
    chats: [blankChat(1, null)],
    activeId: 1,
    nextLocalId: 2,
    inFlight: null,
    capabilities: { byProject: {} },
    context: emptyContext(),
    activeTool: null,
    runOptions: { ...NO_RUN_OPTIONS, status: 'idle', roster: null },
    sidebarRail: false,
  };
}

/* ---- selectors ---- */

export const activeChat = (state: CenterlineAiState): Chat | undefined =>
  state.chats.find((c) => c.localId === state.activeId) ?? state.chats[0];

/**
 * Whether the active chat shows the welcome screen: nothing sent yet and no transcript restored.
 * The composer sits right under it until the first question is sent, then docks at the bottom.
 */
export const showsWelcome = (state: CenterlineAiState): boolean => {
  const chat = activeChat(state);
  return !!chat && chat.welcome && chat.turns.length === 0;
};

export const findTurn = (
  state: CenterlineAiState,
  chatId: number,
  turnKey: string,
): { chat: Chat; turn: Turn } | undefined => {
  const chat = state.chats.find((c) => c.localId === chatId);
  const turn = chat?.turns.find((t) => t.key === turnKey);
  return chat && turn ? { chat, turn } : undefined;
};

export const toolGate = (state: CenterlineAiState): ToolGate => ({
  hasSession: !!activeChat(state)?.sessionId,
  flags: state.projectId ? state.capabilities.byProject[state.projectId] : undefined,
  dbref: state.capabilities.dbref,
});

export const isBusy = (state: CenterlineAiState): boolean => state.inFlight !== null;

/** Whether a turn is still waiting for its answer. */
export const isLive = (bot: BotState): boolean =>
  bot.status === 'pending' || bot.status === 'streaming';

/* ---- helpers ---- */

/** New chat (§3.5): an untitled active chat is reused; otherwise a fresh one goes on top. */
function withNewChat(state: CenterlineAiState): CenterlineAiState {
  const current = activeChat(state);
  if (current && current.title === null) {
    return {
      ...state,
      activeId: current.localId,
      chats: state.chats.map((c) =>
        c.localId === current.localId && !c.sessionId ? { ...c, projectId: state.projectId } : c,
      ),
    };
  }
  const chat = blankChat(state.nextLocalId, state.projectId);
  return {
    ...state,
    chats: [chat, ...state.chats],
    activeId: chat.localId,
    nextLocalId: state.nextLocalId + 1,
  };
}

const updateChat = (
  state: CenterlineAiState,
  localId: number,
  update: (chat: Chat) => Chat,
): CenterlineAiState => {
  let changed = false;
  const chats = state.chats.map((c) => {
    if (c.localId !== localId) return c;
    changed = true;
    return update(c);
  });
  return changed ? { ...state, chats } : state;
};

const updateBot = (
  state: CenterlineAiState,
  chatId: number,
  turnKey: string,
  update: (bot: BotState, turn: Turn, chat: Chat) => BotState,
): CenterlineAiState =>
  updateChat(state, chatId, (chat) => ({
    ...chat,
    turns: chat.turns.map((t) => (t.key === turnKey ? { ...t, bot: update(t.bot, t, chat) } : t)),
  }));

const fail = (bot: BotState, message: string): BotState => ({
  ...bot,
  status: 'error',
  message,
  summary: failedSummary(bot.doneSteps),
  traceVisible: true,
});

const stop = (bot: BotState): BotState => ({
  ...bot,
  status: 'stopped',
  answer: bot.streamedText ? `${bot.streamedText}\n\n*(stopped)*` : undefined,
  message: bot.streamedText ? undefined : STOPPED_EMPTY_TEXT,
  traceVisible: true,
  traceOpen: false,
  summary: stoppedSummary(bot.doneSteps),
});

const updateStep = (
  trace: TraceItem[],
  i: number,
  update: (step: TraceStep) => TraceStep,
): { trace: TraceItem[]; found: boolean } => {
  let index = -1;
  trace.forEach((item, k) => {
    if (item.kind === 'step' && item.i === i) index = k;
  });
  if (index < 0) return { trace, found: false };
  const next = [...trace];
  next[index] = update(trace[index] as TraceStep);
  return { trace: next, found: true };
};

const IMAGE_TYPE_RE = /^image\/[a-z0-9.+-]+$/i;
const BASE64_RE = /^[A-Za-z0-9+/=\s]+$/;

/** A stored transcript turn, drawn the same way as a live one (§3.4, contract §4.2). */
export function restoreTurn(stored: StoredTurn, index: number): Turn {
  const failed = stored.status === 'error' || stored.status === 'aborted' || !stored.answer;
  const base = newLiveBot();
  const turnId = serverId(stored.turn_id) ?? undefined;
  const steps = Array.isArray(stored.steps) ? stored.steps : [];
  const reasoning = Array.isArray(stored.reasoning) ? stored.reasoning : [];
  const bot: BotState = failed
    ? {
        ...base,
        status: 'error',
        restored: true,
        errorRow: true,
        message:
          stored.answer ||
          (stored.status === 'aborted'
            ? 'This turn was stopped.'
            : 'This turn ended with an error.'),
        traceVisible: false,
        traceOpen: false,
      }
    : {
        ...base,
        status: 'done',
        restored: true,
        answer: stored.answer ?? '',
        citations: Array.isArray(stored.citations) ? stored.citations : [],
        trace: restoredTrace({ steps, reasoning }),
        traceVisible: steps.length > 0 || reasoning.length > 0,
        traceOpen: false,
        summary: restoredSummary({ steps }),
        usage: usageFromTurn(stored) ?? undefined,
        turnId,
        feedback: {
          rating: stored.rating === 1 || stored.rating === -1 ? stored.rating : null,
          reason: stored.reason ?? null,
          note: '',
        },
        showFeedback: !!turnId,
      };
  return {
    key: `r:${turnId ?? index}`,
    question: str(stored.question),
    toolUsed: null,
    contextUsed: null,
    projectId: serverId(stored.project_id),
    bot,
  };
}

/** One stream frame (§5.5). Only `title` still applies once the turn has settled. */
function applyFrame(
  state: CenterlineAiState,
  chatId: number,
  turnKey: string,
  frame: SseFrame,
): CenterlineAiState {
  const found = findTurn(state, chatId, turnKey);
  if (!found) return state;
  const d = frame.data;

  if (frame.event === 'title') {
    const title = str(d.title).trim();
    return title ? updateChat(state, chatId, (c) => ({ ...c, title })) : state;
  }
  if (!isLive(found.turn.bot)) return state;

  switch (frame.event) {
    case 'start': {
      const sessionId = serverId(d.session_id);
      const turnProject = found.turn.projectId;
      let next = updateChat(state, chatId, (c) => ({
        ...c,
        sessionId: sessionId ?? c.sessionId,
        projectId: turnProject ?? c.projectId,
      }));
      next = updateBot(next, chatId, turnKey, (bot) => ({
        ...bot,
        turnId: serverId(d.turn_id) ?? undefined,
        budget: num(d.maxSteps),
      }));
      const flagProject = str(d.projectId) || turnProject;
      const flags: ProjectToolFlags = {};
      (['search_text', 'drawings', 'specifications', 'generic'] as const).forEach((k) => {
        if (typeof d[k] === 'boolean') flags[k] = d[k] as boolean;
      });
      return {
        ...next,
        capabilities: {
          byProject: flagProject
            ? { ...next.capabilities.byProject, [flagProject]: flags }
            : next.capabilities.byProject,
          dbref: typeof d.dbref === 'boolean' ? d.dbref : next.capabilities.dbref,
        },
      };
    }
    case 'turn':
      return updateBot(state, chatId, turnKey, (bot) => ({
        ...bot,
        batch: { calls: num(d.calls) || 1, seen: 0 },
      }));
    case 'step_start':
      return updateBot(state, chatId, turnKey, (bot) => {
        const i = num(d.i) ?? bot.trace.filter((t) => t.kind === 'step').length;
        const tool = str(d.tool);
        const seen = bot.batch.seen + 1;
        const parallel = bot.batch.calls > 1;
        const step: TraceStep = {
          kind: 'step',
          i,
          tool,
          purpose: str(d.purpose),
          code: (tool === 'run_sql' ? str(d.sql) : str(d.query)) || undefined,
          status: 'running',
          count: 0,
          images: [],
          parallel,
          batchStart: parallel && seen === 1,
          ...(parallel ? parallelFlow(seen, bot.batch.calls) : chainFlow(i)),
        };
        return {
          ...bot,
          batch: { ...bot.batch, seen },
          summary: stepProgress(i, bot.budget),
          trace: [...bot.trace, step],
        };
      });
    case 'image':
      return updateBot(state, chatId, turnKey, (bot) => {
        const base64 = str(d.base64);
        if (!base64 || !BASE64_RE.test(base64)) return bot;
        const mediaType = IMAGE_TYPE_RE.test(str(d.mediaType)) ? str(d.mediaType) : 'image/png';
        const { trace } = updateStep(bot.trace, num(d.i) ?? -1, (s) => ({
          ...s,
          images: [...s.images, `data:${mediaType};base64,${base64.replace(/\s+/g, '')}`],
        }));
        return { ...bot, trace };
      });
    case 'step_end':
      return updateBot(state, chatId, turnKey, (bot) => {
        const error = str(d.error);
        const { trace, found: hit } = updateStep(bot.trace, num(d.i) ?? -1, (s) => ({
          ...s,
          status: error ? 'error' : 'done',
          count: liveCount({ rowCount: num(d.rowCount), matchCount: num(d.matchCount) }),
          error: error || undefined,
        }));
        return {
          ...bot,
          trace,
          doneSteps: hit ? bot.doneSteps + 1 : bot.doneSteps,
          summary: THINKING,
        };
      });
    case 'reasoning':
      return updateBot(state, chatId, turnKey, (bot) => {
        const text = str(d.text);
        const summary = bot.summary === PLANNING ? THINKING : bot.summary;
        const last = bot.trace[bot.trace.length - 1];
        if (last && last.kind === 'thinking') {
          return {
            ...bot,
            summary,
            trace: [...bot.trace.slice(0, -1), { ...last, text: last.text + text }],
          };
        }
        if (!text) return { ...bot, summary };
        return { ...bot, summary, trace: [...bot.trace, { kind: 'thinking', text }] };
      });
    case 'delta':
      return applyDelta(state, chatId, turnKey, str(d.text));
    case 'usage': {
      const usage = d.usage;
      if (typeof usage !== 'object' || usage === null) return state;
      return updateBot(state, chatId, turnKey, (bot) => ({ ...bot, usage: usage as AgentUsage }));
    }
    case 'answer':
      return updateBot(state, chatId, turnKey, (bot) => {
        const steps = Array.isArray(d.steps) ? (d.steps as AgentStep[]) : [];
        const citations = Array.isArray(d.citations)
          ? (d.citations as Partial<CitationInfo>[])
          : [];
        const liveSteps = bot.trace.filter((t): t is TraceStep => t.kind === 'step');
        const noSteps = steps.length === 0 && bot.doneSteps === 0;
        return {
          ...bot,
          status: 'done',
          answer: str(d.response) || NO_ANSWER_TEXT,
          citations,
          // A turn that ran no tools drops its trace, reasoning and all (§5.9, quirk 4).
          traceVisible: !noSteps,
          traceOpen: false,
          summary: noSteps ? bot.summary : finishedSummary(steps.length ? steps : liveSteps),
          showFeedback: !!bot.turnId,
        };
      });
    case 'error': {
      const detail = str(d.detail);
      const message = `${str(d.error) || 'Sorry, I could not answer that question.'}${
        detail ? ` (${detail})` : ''
      }`;
      return updateBot(state, chatId, turnKey, (bot) => fail(bot, message));
    }
    default:
      // `done` and anything unknown.
      return state;
  }
}

function applyDelta(
  state: CenterlineAiState,
  chatId: number,
  turnKey: string,
  text: string,
): CenterlineAiState {
  if (!text) return state;
  return updateBot(state, chatId, turnKey, (bot) =>
    isLive(bot) ? { ...bot, status: 'streaming', streamedText: bot.streamedText + text } : bot,
  );
}

export function chatReducer(
  state: CenterlineAiState,
  action: CenterlineAiAction,
): CenterlineAiState {
  switch (action.type) {
    case 'projectSelected': {
      if (action.projectId === state.projectId) return state;
      // A project change clears the "@" scope, starts a new chat and reloads the list (§2.5).
      return withNewChat({ ...state, projectId: action.projectId, context: emptyContext() });
    }
    case 'newChat':
      return withNewChat(state);
    case 'openChat':
      return state.chats.some((c) => c.localId === action.localId)
        ? { ...state, activeId: action.localId }
        : state;
    case 'sessionsLoaded': {
      if (action.projectId !== state.projectId) return state;
      const kept = state.chats.filter((c) => !c.sessionId || c.localId === state.activeId);
      const present = new Set(kept.map((c) => c.sessionId).filter(Boolean));
      let nextLocalId = state.nextLocalId;
      const added: Chat[] = [];
      action.sessions.forEach((s) => {
        const sessionId = serverId(s?.session_id);
        if (!sessionId || present.has(sessionId)) return;
        present.add(sessionId);
        added.push({
          localId: nextLocalId,
          sessionId,
          projectId: action.projectId,
          title: typeof s.title === 'string' && s.title ? s.title : null,
          loaded: false,
          welcome: true,
          turns: [],
        });
        nextLocalId += 1;
      });
      return { ...state, chats: [...kept, ...added], nextLocalId };
    }
    case 'sessionRestored': {
      // A stored session opened directly (the document review pane), not through the sidebar list.
      if (action.projectId !== state.projectId) return state;
      const existing = state.chats.find((c) => c.sessionId === action.sessionId);
      const turns = action.turns.map((t, index) => restoreTurn(t, index));
      if (existing) {
        return updateChat({ ...state, activeId: existing.localId }, existing.localId, (c) => ({
          ...c,
          loaded: true,
          welcome: false,
          turns,
        }));
      }
      const chat: Chat = {
        ...blankChat(state.nextLocalId, action.projectId),
        sessionId: action.sessionId,
        title: action.turns.length ? provisionalTitle(action.turns[0].question) : null,
        loaded: true,
        welcome: false,
        turns,
      };
      return {
        ...state,
        chats: [chat, ...state.chats],
        activeId: chat.localId,
        nextLocalId: state.nextLocalId + 1,
      };
    }
    case 'transcriptRequested':
      return updateChat(state, action.localId, (c) => ({ ...c, loaded: true }));
    case 'transcriptLoaded':
      return updateChat(state, action.localId, (c) => ({
        ...c,
        welcome: false,
        turns: action.turns.map((t, index) => restoreTurn(t, index)),
      }));
    case 'renameChat': {
      const title = renamedTitle(action.title);
      return title === null ? state : updateChat(state, action.localId, (c) => ({ ...c, title }));
    }
    case 'deleteChat': {
      const index = state.chats.findIndex((c) => c.localId === action.localId);
      if (index < 0) return state;
      const chats = state.chats.filter((c) => c.localId !== action.localId);
      if (chats.length === 0) {
        const chat = blankChat(state.nextLocalId, state.projectId);
        return {
          ...state,
          chats: [chat],
          activeId: chat.localId,
          nextLocalId: state.nextLocalId + 1,
        };
      }
      if (action.localId !== state.activeId) return { ...state, chats };
      return { ...state, chats, activeId: chats[Math.min(index, chats.length - 1)].localId };
    }
    case 'toggleRail':
      return { ...state, sidebarRail: !state.sidebarRail };
    case 'send': {
      if (state.inFlight) return state;
      const chat = state.chats.find((c) => c.localId === action.chatId);
      if (!chat) return state;
      const turn: Turn = {
        key: action.turnKey,
        question: action.question,
        toolUsed: isPickable(action.tool) ? action.tool : null,
        contextUsed:
          action.context && !isEmptySelection(action.context)
            ? contextTagLabels(action.context)
            : null,
        projectId: state.projectId,
        bot: newLiveBot(),
      };
      const next = updateChat(state, action.chatId, (c) => ({
        ...c,
        title: c.title ?? provisionalTitle(action.question),
        welcome: false,
        turns: [...c.turns, turn],
      }));
      return {
        ...next,
        inFlight: { chatId: action.chatId, turnKey: action.turnKey },
        activeTool: null,
        context: { ...next.context, pickerOpen: false },
      };
    }
    case 'frame':
      return applyFrame(state, action.chatId, action.turnKey, action.frame);
    case 'delta':
      return applyDelta(state, action.chatId, action.turnKey, action.text);
    case 'turnFailed':
      return updateBot(state, action.chatId, action.turnKey, (bot) =>
        isLive(bot) ? fail(bot, action.message) : bot,
      );
    case 'turnStopped':
      return updateBot(state, action.chatId, action.turnKey, (bot) =>
        isLive(bot) ? stop(bot) : bot,
      );
    case 'turnSettled': {
      // The stream ended without an answer or an error: a stall (§5.5).
      const next = updateBot(state, action.chatId, action.turnKey, (bot) =>
        isLive(bot) ? fail(bot, STALLED_TEXT) : bot,
      );
      const settled =
        next.inFlight?.chatId === action.chatId && next.inFlight.turnKey === action.turnKey;
      return settled ? { ...next, inFlight: null } : next;
    }
    case 'feedback':
      return updateBot(state, action.chatId, action.turnKey, (bot) => ({
        ...bot,
        feedback: { ...bot.feedback, ...action.patch },
      }));
    case 'setTool':
      return { ...state, activeTool: isPickable(action.tool) ? action.tool : null };
    case 'ctxToggleType':
      return {
        ...state,
        context: {
          ...state.context,
          selection: toggleType(state.context.selection, action.docType, state.context.fileCache),
        },
      };
    case 'ctxToggleFile':
      return {
        ...state,
        context: { ...state.context, selection: toggleFile(state.context.selection, action.file) },
      };
    case 'ctxRemoveType':
      return {
        ...state,
        context: {
          ...state.context,
          selection: removeType(state.context.selection, action.docType),
        },
      };
    case 'ctxRemoveFile':
      return {
        ...state,
        context: {
          ...state.context,
          selection: removeFile(state.context.selection, action.fileId),
        },
      };
    case 'ctxClear':
      return { ...state, context: { ...state.context, selection: EMPTY_SELECTION } };
    case 'ctxDocTypesLoaded':
      if (action.projectId !== state.projectId) return state;
      return {
        ...state,
        context: {
          ...state.context,
          docTypes: action.docTypes,
          docTypesFor: action.cache ? action.projectId : null,
        },
      };
    case 'ctxFilesLoaded':
      if (action.projectId !== state.projectId) return state;
      return {
        ...state,
        context: {
          ...state.context,
          fileCache: { ...state.context.fileCache, [action.key]: action.files },
        },
      };
    case 'ctxFilter':
      return { ...state, context: { ...state.context, docFilter: action.docType } };
    case 'ctxPicker':
      return state.context.pickerOpen === action.open
        ? state
        : { ...state, context: { ...state.context, pickerOpen: action.open } };
    case 'rosterLoading':
      return { ...state, runOptions: { ...state.runOptions, status: 'loading' } };
    case 'rosterLoaded':
      return {
        ...state,
        runOptions: { ...action.options, status: 'ready', roster: action.roster },
      };
    case 'rosterFailed':
      return { ...state, runOptions: { ...NO_RUN_OPTIONS, status: 'failed', roster: null } };
    case 'setRunOption':
      return { ...state, runOptions: { ...state.runOptions, [action.kind]: action.value } };
    default:
      return state;
  }
}
